import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import paymentService from '../services/paymentService';
import './MyCourses.css';

const MyCourses = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters & Search
  const [filterMode, setFilterMode] = useState('all'); // 'all', 'online', 'offline'
  const [searchQuery, setSearchQuery] = useState('');

  // Selected receipt for detailed modal view
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [copiedReceipt, setCopiedReceipt] = useState(null);

  // Fetch enrollments on mount or when user changes
  useEffect(() => {
    let isMounted = true;

    const fetchEnrollments = async () => {
      if (!isAuthenticated && !authLoading) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await paymentService.getMyEnrollments();
        if (isMounted) {
          if (data && data.success && Array.isArray(data.enrollments)) {
            setEnrollments(data.enrollments);
          } else {
            setEnrollments([]);
          }
        }
      } catch (err) {
        if (isMounted) {
          console.error('Failed to load student enrollments:', err);
          setError(err.message || 'Unable to retrieve your cohort enrollments.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (!authLoading) {
      fetchEnrollments();
    }

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, authLoading]);

  // Handle receipt copy
  const handleCopyReceipt = (receiptNum) => {
    if (!receiptNum) return;
    navigator.clipboard.writeText(receiptNum);
    setCopiedReceipt(receiptNum);
    setTimeout(() => {
      setCopiedReceipt(null);
    }, 2000);
  };

  // Filtered list
  const filteredEnrollments = enrollments.filter((item) => {
    const matchesMode =
      filterMode === 'all' ||
      (filterMode === 'online' && item.mode === 'online') ||
      (filterMode === 'offline' && item.mode === 'offline');

    const courseTitle = item.courseTitle || item.courseId?.title || '';
    const receiptNum = item.receiptNumber || '';
    const matchesSearch =
      courseTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
      receiptNum.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesMode && matchesSearch;
  });

  // Calculate statistics
  const totalEnrolled = enrollments.length;
  const onlineCount = enrollments.filter((e) => e.mode === 'online').length;
  const offlineCount = enrollments.filter((e) => e.mode === 'offline').length;
  const totalInvested = enrollments.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  // If user is not authenticated and auth check finished
  if (!authLoading && !isAuthenticated) {
    return (
      <div className="my-courses-page">
        <section className="my-courses-hero">
          <div className="my-courses-hero-container">
            <div className="my-courses-user-text">
              <h1>Student Learning Dashboard</h1>
              <p style={{ color: '#cbd5e1', margin: 0 }}>
                Please sign in to access your registered engineering cohorts, classroom tracks, and fee receipts.
              </p>
            </div>
          </div>
        </section>

        <div className="my-courses-content">
          <div className="my-courses-empty">
            <div className="empty-icon">🔐</div>
            <h2 className="empty-title">Authentication Required</h2>
            <p className="empty-desc">
              Sign in with your registered student account to view enrolled cohorts and verify your payment receipts.
            </p>
            <Link to="/login" className="btn-explore-cohorts">
              Sign In to Your Account &rarr;
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-courses-page">
      {/* Hero / Profile Summary Bar */}
      <section className="my-courses-hero">
        <div className="my-courses-hero-container">
          <div className="my-courses-profile-bar">
            {/* User Greeting */}
            <div className="my-courses-user-info">
              <div className="my-courses-avatar">
                {user?.name
                  ? user.name.charAt(0).toUpperCase()
                  : (user?.username || user?.email || 'S').charAt(0).toUpperCase()}
              </div>
              <div className="my-courses-user-text">
                <h1>Welcome back, {user?.name || user?.username || 'Engineer'}!</h1>
                <div className="my-courses-user-meta">
                  <span>📧 {user?.email}</span>
                  {user?.phone && <span>📞 {user.phone}</span>}
                  <span className="my-courses-badge badge-verified">
                    ✓ Verified Student
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="my-courses-stats">
              <div className="my-courses-stat-card">
                <div className="stat-label">Active Cohorts</div>
                <div className="stat-value">{totalEnrolled}</div>
              </div>
              <div className="my-courses-stat-card">
                <div className="stat-label">Online Track</div>
                <div className="stat-value">{onlineCount}</div>
              </div>
              <div className="my-courses-stat-card">
                <div className="stat-label">Jaipur Campus</div>
                <div className="stat-value">{offlineCount}</div>
              </div>
              <div className="my-courses-stat-card">
                <div className="stat-label">Invested</div>
                <div className="stat-value">₹{totalInvested.toLocaleString('en-IN')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <main className="my-courses-content">
        {/* Controls Toolbar */}
        <div className="my-courses-toolbar">
          <div className="filter-tabs">
            <button
              type="button"
              className={`filter-tab-btn ${filterMode === 'all' ? 'active' : ''}`}
              onClick={() => setFilterMode('all')}
            >
              All Cohorts ({totalEnrolled})
            </button>
            <button
              type="button"
              className={`filter-tab-btn ${filterMode === 'online' ? 'active' : ''}`}
              onClick={() => setFilterMode('online')}
            >
              🌐 Online ({onlineCount})
            </button>
            <button
              type="button"
              className={`filter-tab-btn ${filterMode === 'offline' ? 'active' : ''}`}
              onClick={() => setFilterMode('offline')}
            >
              🏢 Jaipur Campus ({offlineCount})
            </button>
          </div>

          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search cohort or receipt #..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Loading Spinner */}
        {loading && (
          <div className="my-courses-loading">
            <div className="spinner"></div>
            <p>Loading your enrolled cohorts and receipts...</p>
          </div>
        )}

        {/* Error Notice */}
        {!loading && error && (
          <div className="my-courses-empty">
            <div className="empty-icon">⚠️</div>
            <h2 className="empty-title">Failed to load courses</h2>
            <p className="empty-desc">{error}</p>
            <button
              type="button"
              className="btn-explore-cohorts"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty State when 0 enrolled */}
        {!loading && !error && enrollments.length === 0 && (
          <div className="my-courses-empty">
            <div className="empty-icon">📚</div>
            <h2 className="empty-title">No Enrolled Courses Found</h2>
            <p className="empty-desc">
              You haven't enrolled in any Gurukulam cohorts or classroom batches yet.
              Join our flagship industry engineering tracks to build production systems with senior mentors.
            </p>
            <Link to="/courses" className="btn-explore-cohorts">
              Browse Available Cohorts &rarr;
            </Link>
          </div>
        )}

        {/* Filter Zero Results */}
        {!loading && !error && enrollments.length > 0 && filteredEnrollments.length === 0 && (
          <div className="my-courses-empty">
            <div className="empty-icon">🔍</div>
            <h2 className="empty-title">No matching courses</h2>
            <p className="empty-desc">
              No enrolled programs match your current filter or search criteria.
            </p>
            <button
              type="button"
              className="btn-explore-cohorts"
              onClick={() => {
                setFilterMode('all');
                setSearchQuery('');
              }}
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Courses Grid */}
        {!loading && !error && filteredEnrollments.length > 0 && (
          <div className="my-courses-grid">
            {filteredEnrollments.map((enrollment) => {
              const course = enrollment.courseId || {};
              const courseTitle = enrollment.courseTitle || course.title || 'Engineering Cohort';
              const coursePath = course.path || course._id;
              const isOffline = enrollment.mode === 'offline';

              return (
                <article key={enrollment._id} className="enrolled-card">
                  {/* Card Header & Badges */}
                  <div className="enrolled-card-header">
                    {course.image ? (
                      <img
                        src={course.image}
                        alt={courseTitle}
                        className="enrolled-card-img"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : null}

                    {/* Mode Badge */}
                    <div className={`card-track-badge ${isOffline ? 'track-offline' : 'track-online'}`}>
                      {isOffline ? (
                        <>🏢 Jaipur Campus</>
                      ) : (
                        <>
                          <span className="pulse-dot"></span>
                          🌐 Live Online Cohort
                        </>
                      )}
                    </div>

                    <div className="card-status-badge">Active Enrollment</div>
                  </div>

                  {/* Card Body */}
                  <div className="enrolled-card-body">
                    <h2 className="enrolled-title">{courseTitle}</h2>

                    <div className="enrolled-meta-row">
                      {course.instructor && (
                        <span>👨‍🏫 {course.instructor}</span>
                      )}
                      {course.duration && (
                        <span>⏳ {course.duration}</span>
                      )}
                      {course.level && (
                        <span>📊 {course.level}</span>
                      )}
                    </div>

                    {/* Track info banner */}
                    <div className={`enrolled-track-note ${isOffline ? 'note-offline' : 'note-online'}`}>
                      {isOffline ? (
                        <div>
                          📍 <strong>Campus Location:</strong> Radha Vihar, Manchwa, Jaipur.<br />
                          Counseling Desk: +91 7852034945
                        </div>
                      ) : (
                        <div>
                          💻 <strong>Cohort Hub:</strong> Live session links, mentor office hours &amp; Discord access dispatched to {enrollment.studentEmail}.
                        </div>
                      )}
                    </div>

                    {/* Payment Info Box */}
                    <div className="enrolled-payment-bar">
                      <div className="payment-row">
                        <span className="payment-label">Fee Paid:</span>
                        <span className="payment-val payment-val-price">
                          ₹{Number(enrollment.amount || 0).toLocaleString('en-IN')} {enrollment.currency || 'INR'}
                        </span>
                      </div>
                      <div className="payment-row">
                        <span className="payment-label">Enrolled On:</span>
                        <span className="payment-val">{formatDate(enrollment.createdAt)}</span>
                      </div>
                      <div className="payment-row">
                        <span className="payment-label">Access Duration:</span>
                        <span className="payment-val" style={{ fontWeight: 600 }}>
                          {enrollment.validityDuration || (course.duration ? `${course.duration} Access` : 'Full Course Access')}
                        </span>
                      </div>
                      {enrollment.validUntil && (
                        <div className="payment-row">
                          <span className="payment-label">Valid Till:</span>
                          <span
                            className="payment-val"
                            style={{
                              fontWeight: 600,
                              color: new Date(enrollment.validUntil) < new Date() ? '#ef4444' : '#10b981'
                            }}
                          >
                            {formatDate(enrollment.validUntil)}
                            {new Date(enrollment.validUntil) < new Date() ? ' (Expired)' : ' (Active)'}
                          </span>
                        </div>
                      )}
                      <div className="payment-row">
                        <span className="payment-label">Receipt #:</span>
                        <span
                          className="receipt-code"
                          title="Click to copy receipt number"
                          onClick={() => handleCopyReceipt(enrollment.receiptNumber)}
                        >
                          {enrollment.receiptNumber || 'N/A'}{' '}
                          {copiedReceipt === enrollment.receiptNumber ? '✓ Copied' : '📋'}
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="enrolled-card-actions">
                      {coursePath ? (
                        <Link
                          to={`/courses/${coursePath}`}
                          className="btn-cohort-hub"
                        >
                          🚀 Access Cohort Hub
                        </Link>
                      ) : (
                        <button
                          type="button"
                          className="btn-cohort-hub"
                          onClick={() => navigate('/courses')}
                        >
                          🚀 Cohort Overview
                        </button>
                      )}

                      <button
                        type="button"
                        className="btn-receipt-modal"
                        onClick={() => setSelectedReceipt(enrollment)}
                      >
                        🧾 Tax Receipt
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>

      {/* Verifiable Tax Receipt Modal */}
      {selectedReceipt && (
        <div
          className="receipt-modal-backdrop"
          onClick={() => setSelectedReceipt(null)}
        >
          <div
            className="receipt-modal-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="receipt-modal-header">
              <h3 className="receipt-modal-title">
                <span>🧾</span> Official Fee Payment Receipt
              </h3>
              <button
                type="button"
                className="modal-close-icon"
                onClick={() => setSelectedReceipt(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            {/* Printable Sheet */}
            <div className="receipt-sheet" id="printable-tax-receipt">
              {/* Brand Header */}
              <div className="receipt-brand-header">
                <div>
                  <div className="receipt-brand-title">Durbhasi Gurukulam Pvt Ltd</div>
                  <div className="receipt-brand-sub">
                    Institute of Systems &amp; Software Engineering<br />
                    Jaipur Campus: P.No. 18, Radha Vihar, Manchwa, Jaipur, Rajasthan 303706<br />
                    Helpline: +91 7852034945 | admissions@durbhasigurukulam.com
                  </div>
                </div>
                <div className="receipt-seal-badge">
                  ✓ VERIFIED ENROLLMENT<br />
                  <span style={{ fontSize: '0.68rem', fontWeight: 500, color: '#047857' }}>
                    Payment Captured
                  </span>
                </div>
              </div>

              {/* Meta Grid */}
              <div className="receipt-meta-grid">
                <div className="receipt-meta-cell">
                  <div className="cell-label">Receipt Number</div>
                  <div className="cell-val" style={{ fontFamily: 'monospace' }}>
                    {selectedReceipt.receiptNumber || 'N/A'}
                  </div>
                </div>
                <div className="receipt-meta-cell">
                  <div className="cell-label">Date of Payment</div>
                  <div className="cell-val">{formatDate(selectedReceipt.createdAt)}</div>
                </div>
                <div className="receipt-meta-cell">
                  <div className="cell-label">Student Name</div>
                  <div className="cell-val">{selectedReceipt.studentName}</div>
                </div>
                <div className="receipt-meta-cell">
                  <div className="cell-label">Student Contact</div>
                  <div className="cell-val">
                    {selectedReceipt.studentEmail}<br />
                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      {selectedReceipt.studentPhone}
                    </span>
                  </div>
                </div>
                {selectedReceipt.validityDuration && (
                  <div className="receipt-meta-cell">
                    <div className="cell-label">Access Validity</div>
                    <div className="cell-val" style={{ color: '#059669', fontWeight: 600 }}>
                      {selectedReceipt.validityDuration}
                      {selectedReceipt.validUntil && (
                        <div style={{ fontSize: '0.8rem', color: '#475569', fontWeight: 400 }}>
                          Valid till: {formatDate(selectedReceipt.validUntil)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Itemized Table */}
              <table className="receipt-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Track / Mode</th>
                    <th className="text-right">Total (INR)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <strong>{selectedReceipt.courseTitle}</strong>
                      <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>
                        Curriculum access, live sessions, project reviews &amp; career guidance.
                      </div>
                    </td>
                    <td>
                      {selectedReceipt.mode === 'offline' ? (
                        <span style={{ color: '#d97706', fontWeight: 600 }}>
                          🏢 Jaipur Classroom Batch
                        </span>
                      ) : (
                        <span style={{ color: '#2563eb', fontWeight: 600 }}>
                          🌐 Live Online Cohort
                        </span>
                      )}
                    </td>
                    <td className="text-right" style={{ fontWeight: 700 }}>
                      ₹{Number(selectedReceipt.amount || 0).toLocaleString('en-IN')}
                    </td>
                  </tr>
                  <tr className="receipt-total-row">
                    <td colSpan="2">
                      Total Amount Paid (Taxes Included)
                    </td>
                    <td className="text-right">
                      ₹{Number(selectedReceipt.amount || 0).toLocaleString('en-IN')}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Gateway References */}
              <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '12px' }}>
                <div><strong>Razorpay Payment ID:</strong> <span style={{ fontFamily: 'monospace' }}>{selectedReceipt.razorpayPaymentId || 'N/A'}</span></div>
                <div><strong>Order Reference:</strong> <span style={{ fontFamily: 'monospace' }}>{selectedReceipt.razorpayOrderId || 'N/A'}</span></div>
              </div>

              {/* Notes */}
              <div className="receipt-footer-notes">
                This is a digitally generated tax receipt issued by Durbhasi Gurukulam Pvt Ltd. No physical signature is required. Keep this receipt for your learning tax deduction and campus orientation records.
              </div>
            </div>

            {/* Modal Actions */}
            <div className="receipt-modal-actions">
              <button
                type="button"
                className="btn-modal-close"
                onClick={() => setSelectedReceipt(null)}
              >
                Close
              </button>
              <button
                type="button"
                className="btn-modal-print"
                onClick={() => window.print()}
              >
                🖨️ Print / Save PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCourses;
